import { API_URL } from "./env";
import type {
  AuditPage,
  CheckoutSession,
  Connector,
  CreateConnectorInput,
  CreateDestinationInput,
  CreateKeyInput,
  CreateKeyResult,
  Destination,
  UpdateConnectorInput,
  UpdateDestinationInput,
  ConnectorMembersList,
  SetConnectorMembersInput,
  OrgMembersList,
  Me,
  ProcessedStats,
  ProductId,
  Tenancy,
  TestConnectorResult,
  UploadKey,
  Usage,
} from "./types";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

// Typed fetch wrapper. Always sends cookies (session lives on the API origin),
// throws ApiError on non-2xx so React Query surfaces it consistently.
// Errors are shaped { error: string, details?: ... }.
// The OpsBelt id_token is short-lived and can't be refreshed (Better Auth's
// refresh grant returns no id_token). When it lapses, bounce through OpsBelt
// login — the long OpsBelt session makes that round-trip silent (no prompt) and
// returns the user to where they were. Single-flight so a burst of 401s triggers
// exactly one redirect.
let reauthing = false;
function reauth(): void {
  // Only auto-bounce when we'd previously authenticated (an expired session) —
  // a brand-new visitor should land on BlobShip's branded /login instead.
  const hadSession = sessionStorage.getItem("bs_authed") === "1";
  if (reauthing || !hadSession || window.location.pathname === "/login") return;
  reauthing = true;
  const returnTo = window.location.pathname + window.location.search;
  window.location.href = `${API_URL}/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: opts.method ?? "GET",
    credentials: "include",
    headers: opts.body ? { "Content-Type": "application/json" } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  // Remember we have a working session; an expired token then triggers a silent
  // re-auth through OpsBelt (vs showing login to someone who never signed in).
  if (res.ok) sessionStorage.setItem("bs_authed", "1");
  if (res.status === 401) reauth();

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message, data);
  }

  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// --- Endpoint methods (grouped to match the API surface) ---
// List/create responses are enveloped (e.g. { connectors }, { connector }); we
// unwrap them here so callers work with plain values.
export const api = {
  // Identity lives on OpsBelt; BlobShip surfaces the verified-token claims.
  me: () => request<Me>("/api/auth/me"),
  usage: () => request<Usage>("/api/usage"),

  // Azure admin-consent: connected tenancies + the consent kick-off URL (a
  // top-level navigation, so the bs_token cookie carries the session).
  azure: {
    tenancies: async () =>
      (await request<{ tenancies: Tenancy[] }>("/api/azure/tenancies")).tenancies,
    consentStartUrl: () => `${API_URL}/api/azure/consent/start`,
  },

  // Org directory (proxied from OpsBelt) — the pool for connection assignment.
  org: {
    members: async () =>
      (await request<OrgMembersList>("/api/org/members")).members,
  },

  connectors: {
    list: async () =>
      (await request<{ connectors: Connector[] }>("/api/connectors"))
        .connectors,
    get: async (id: string) =>
      (await request<{ connector: Connector }>(`/api/connectors/${id}`))
        .connector,
    create: async (input: CreateConnectorInput) =>
      (
        await request<{ connector: Connector }>("/api/connectors", {
          method: "POST",
          body: input,
        })
      ).connector,
    update: async (id: string, input: UpdateConnectorInput) =>
      (
        await request<{ connector: Connector }>(`/api/connectors/${id}`, {
          method: "PATCH",
          body: input,
        })
      ).connector,
    members: async (id: string) =>
      (await request<ConnectorMembersList>(`/api/connectors/${id}/members`))
        .members,
    setMembers: async (id: string, input: SetConnectorMembersInput) =>
      (
        await request<ConnectorMembersList>(`/api/connectors/${id}/members`, {
          method: "PUT",
          body: input,
        })
      ).members,
    remove: (id: string) =>
      request<void>(`/api/connectors/${id}`, { method: "DELETE" }),
    test: (id: string) =>
      request<TestConnectorResult>(`/api/connectors/${id}/test`, {
        method: "POST",
      }),
    containers: async (id: string) =>
      (await request<{ containers: string[] }>(`/api/connectors/${id}/containers`))
        .containers,
  },

  destinations: {
    list: async () =>
      (await request<{ destinations: Destination[] }>("/api/destinations"))
        .destinations,
    get: async (id: string) =>
      (await request<{ destination: Destination }>(`/api/destinations/${id}`))
        .destination,
    create: async (input: CreateDestinationInput) =>
      (
        await request<{ destination: Destination }>("/api/destinations", {
          method: "POST",
          body: input,
        })
      ).destination,
    update: async (id: string, input: UpdateDestinationInput) =>
      (
        await request<{ destination: Destination }>(`/api/destinations/${id}`, {
          method: "PATCH",
          body: input,
        })
      ).destination,
    remove: (id: string) =>
      request<void>(`/api/destinations/${id}`, { method: "DELETE" }),
  },

  keys: {
    list: async () => (await request<{ keys: UploadKey[] }>("/api/keys")).keys,
    create: (input: CreateKeyInput) =>
      request<CreateKeyResult>("/api/keys", { method: "POST", body: input }),
    remove: (id: string) =>
      request<{ id: string; status: "revoked" }>(`/api/keys/${id}`, {
        method: "DELETE",
      }),
  },

  audit: (params?: { limit?: number; before?: number }) => {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.before != null) q.set("before", String(params.before));
    const qs = q.toString();
    return request<AuditPage>(`/api/audit${qs ? `?${qs}` : ""}`);
  },
  // CSV export (paid). Returned as a raw string so the page can download it.
  auditExport: () => request<string>("/api/audit/export"),

  stats: {
    processed: () => request<ProcessedStats>("/api/stats/processed"),
  },

  billing: {
    checkout: (productId: ProductId, seats?: number) =>
      request<CheckoutSession>("/api/billing/checkout", {
        method: "POST",
        body: seats != null ? { productId, seats } : { productId },
      }),
    portal: () =>
      request<CheckoutSession>("/api/billing/portal", { method: "POST" }),
  },
};
